import { useEffect, useState } from "react";
import PatientLayout from "@/components/layout/PatientLayout";
import api from "@/api";
import { toast } from "sonner";
import { CheckCircle, Clock, Pill, XCircle } from "lucide-react";
import { useLanguage } from "@/i18n/index";

export default function PatientOralAdherence() {
  const { language } = useLanguage();
  const [doses, setDoses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const isAr = language === "ar";
  const text = isAr ? {
    back: "العودة",
    title: "علاجي في المنزل",
    subtitle: "أكّد هنا الجرعات التي أخذتها أو التي لم تأخذها.",
    empty: "لا توجد جرعات للتأكيد الآن.",
    taken: "أخذته",
    notTaken: "لم آخذه",
    planned: "مقرر",
    saved: "تم الحفظ",
    status: { pending: "بانتظار التأكيد", missed: "غير مؤكد", taken: "تم أخذه", not_taken: "لم يؤخذ", partial: "جزئي" },
  } : {
    back: "Retour",
    title: "Mon traitement à domicile",
    subtitle: "Confirmez ici les prises prévues ou oubliées.",
    empty: "Aucune prise à confirmer pour le moment.",
    taken: "Pris",
    notTaken: "Pas pris",
    planned: "Prévu",
    saved: "Enregistré",
    status: { pending: "À confirmer", missed: "Non confirmé", taken: "Pris", not_taken: "Pas pris", partial: "Partiel" },
  };

  const fetchDoses = async () => {
    try {
      setLoading(true);
      const res = await api.get("/patient/oral-adherence/today");
      setDoses(res.data?.items || []);
    } catch {
      toast.error(isAr ? "تعذر تحميل العلاج" : "Erreur chargement traitement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDoses(); }, []);

  const validate = async (doseId, status) => {
    try {
      setSavingId(doseId);
      await api.post(`/patient/oral-adherence/${doseId}/validate`, { status, reason: status === "not_taken" ? "other" : null });
      toast.success(text.saved);
      await fetchDoses();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? "تعذر الحفظ" : "Erreur d'enregistrement"));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <PatientLayout>
      <div className="space-y-4" data-testid="patient-oral-adherence-page" dir={isAr ? "rtl" : "ltr"}>
        <div className="rounded-[1.75rem] border border-emerald-100 bg-emerald-50 p-5 text-emerald-950 shadow-sm" data-testid="patient-oral-adherence-header">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-emerald-700 shadow-sm">
            <Pill className="h-6 w-6" />
          </div>
          <h1 className="text-2xl font-black leading-tight" data-testid="patient-oral-adherence-title">{text.title}</h1>
          <p className="mt-2 text-sm font-medium leading-relaxed text-emerald-800" data-testid="patient-oral-adherence-subtitle">{text.subtitle}</p>
        </div>

        {loading ? (
          <div className="py-14 text-center text-sm font-bold text-slate-400" data-testid="patient-oral-adherence-loading">...</div>
        ) : doses.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center" data-testid="patient-oral-adherence-empty">
            <CheckCircle className="mx-auto mb-2 h-10 w-10 text-emerald-500" />
            <p className="text-sm font-bold text-slate-600">{text.empty}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="patient-oral-adherence-dose-list">
            {doses.map((dose) => (
              <div key={dose.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`patient-dose-card-${dose.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-base font-black text-slate-900" data-testid={`patient-dose-molecule-${dose.id}`}>{dose.molecule || text.planned}</p>
                    <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-bold text-slate-500" data-testid={`patient-dose-date-${dose.id}`}>
                      <Clock className="h-3.5 w-3.5" /> {dose.scheduledDate || "—"}
                    </p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-600" data-testid={`patient-dose-status-${dose.id}`}>{text.status[dose.status] || dose.status}</span>
                </div>
                {['pending', 'missed'].includes(dose.status) ? (
                  <div className="mt-4 grid grid-cols-2 gap-2" data-testid={`patient-dose-actions-${dose.id}`}>
                    <button disabled={savingId === dose.id} onClick={() => validate(dose.id, "taken")} data-testid={`patient-dose-taken-${dose.id}`} className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-2xl bg-emerald-700 px-3 text-sm font-black text-white transition-[background-color,opacity] hover:bg-emerald-800 disabled:opacity-50">
                      <CheckCircle className="h-4 w-4" /> {text.taken}
                    </button>
                    <button disabled={savingId === dose.id} onClick={() => validate(dose.id, "not_taken")} data-testid={`patient-dose-not-taken-${dose.id}`} className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 text-sm font-black text-slate-700 transition-[background-color,opacity] hover:bg-slate-50 disabled:opacity-50">
                      <XCircle className="h-4 w-4" /> {text.notTaken}
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </PatientLayout>
  );
}